import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Users,
  User,
  Search,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  PowerIcon,
  UserPlus,
  RefreshCw,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Shield,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  FileText,
  Download,
  TrendingUp,
  UserX,
} from "lucide-react";
import { usersAPI } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

// Import modal components (we'll create these later)
import DeleteSubAccountModal from "../../components/modal/admin/DeleteSubAccountModal";
import EditSubAccountModal from "../../components/modal/admin/EditSubAccountModal";
import SubAccountDetailsModal from "../../components/modal/admin/SubAccountDetailsModal";
import CreateSubAccountModal from "../../components/modal/admin/CreateSubAccountModal";
import ApproveRequestModal from "../../components/modal/admin/ApproveRequestModal";
import ToggleSubAccountStatusModal from "../../components/modal/admin/ToggleSubAccountStatusModal";

const SubAccountManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedSubAccount, setSelectedSubAccount] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedApprovalRequest, setSelectedApprovalRequest] = useState(null);
  const [approvalActionType, setApprovalActionType] = useState("approve");
  const [activeTab, setActiveTab] = useState("sub-accounts");
  const [showToggleStatusModal, setShowToggleStatusModal] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [subAccountsPerPage] = useState(10);

  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  // Helper function to get role display name
  const getRoleDisplayName = (role) => {
    const roleNames = {
      super_admin: "Super Admin",
      issuer: "Issuer",
      custodian: "Custodian",
      facility_agent: "Facility Agent",
      investor: "Investor",
      arranger: "Arranger",
      exchange: "Exchange",
    };
    return roleNames[role] || role;
  };

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Reset pagination when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterStatus]);

  // Fetch sub accounts
  const { data: subAccountsData, isPending: subAccountsLoading } = useQuery({
    queryKey: ["subAccounts", debouncedSearchTerm, filterStatus, currentPage],
    queryFn: async () => {
      try {
        // Use the dedicated sub-accounts API endpoint
        const response = await usersAPI.getSubAccounts({
          search: debouncedSearchTerm || undefined,
          status: filterStatus === "all" ? undefined : filterStatus,
          page: currentPage,
          limit: subAccountsPerPage,
        });

        return (
          response?.data?.data || {
            subAccounts: [],
            pagination: {
              currentPage: 1,
              totalPages: 0,
              totalItems: 0,
              itemsPerPage: subAccountsPerPage,
            },
          }
        );
      } catch (error) {
        console.error("Error fetching sub-accounts:", error);
        // Fallback to regular users API for now - filter for actual sub-accounts
        const response = await usersAPI.getUsers({
          search: debouncedSearchTerm || undefined,
          status: filterStatus === "all" ? undefined : filterStatus,
          limit: 100,
        });

        const allUsers = response?.data?.data?.users || [];

        // Filter for actual sub-accounts (users with isSubAccount flag and parentAccountId)
        const subAccounts = allUsers.filter(
          (user) =>
            user.isSubAccount === true &&
            user.parentAccountId &&
            user.parentAccountId.toString() === currentUser?._id?.toString()
        );

        const sortedSubAccounts = subAccounts.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        const startIndex = (currentPage - 1) * subAccountsPerPage;
        const endIndex = startIndex + subAccountsPerPage;
        const paginatedSubAccounts = sortedSubAccounts.slice(
          startIndex,
          endIndex
        );

        return {
          subAccounts: paginatedSubAccounts,
          pagination: {
            currentPage,
            totalPages: Math.ceil(subAccounts.length / subAccountsPerPage),
            totalItems: subAccounts.length,
            itemsPerPage: subAccountsPerPage,
          },
        };
      }
    },
    select: (data) => data || { subAccounts: [], pagination: {} },
  });

  // Fetch all sub accounts for statistics (without pagination)
  const { data: allSubAccountsData } = useQuery({
    queryKey: ["allSubAccountsStats", debouncedSearchTerm, filterStatus],
    queryFn: async () => {
      try {
        const response = await usersAPI.getSubAccounts({
          search: debouncedSearchTerm || undefined,
          status: filterStatus === "all" ? undefined : filterStatus,
          limit: 1000,
        });

        return response?.data?.data?.subAccounts || [];
      } catch (error) {
        console.error("Error fetching sub-accounts stats:", error);
        // Fallback to regular users API - filter for actual sub-accounts
        const response = await usersAPI.getUsers({
          search: debouncedSearchTerm || undefined,
          status: filterStatus === "all" ? undefined : filterStatus,
          limit: 100,
        });

        const allUsers = response?.data?.data?.users || [];
        // Filter for actual sub-accounts (users with isSubAccount flag and parentAccountId)
        return allUsers.filter(
          (user) =>
            user.isSubAccount === true &&
            user.parentAccountId &&
            user.parentAccountId.toString() === currentUser?._id?.toString()
        );
      }
    },
    select: (data) => data || [],
  });

  // Fetch pending sub-account approval requests
  const {
    data: pendingApprovalsData,
    isLoading: pendingApprovalsLoading,
    error: pendingApprovalsError,
  } = useQuery({
    queryKey: ["sub-account-pending-approvals", activeTab],
    queryFn: () => usersAPI.getPendingSubAccountApprovals(),
    enabled: activeTab === "pending-approvals",
  });

  // Fetch sub-account approval history
  const { data: approvalHistoryData, isLoading: approvalHistoryLoading } =
    useQuery({
      queryKey: ["sub-account-approval-history", activeTab],
      queryFn: () => usersAPI.getSubAccountApprovalHistory(),
      enabled: activeTab === "approval-history",
    });

  // Mutations for sub-account operations
  const deleteSubAccountMutation = useMutation({
    mutationFn: (subAccountId) => usersAPI.deleteSubAccount(subAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["allSubAccountsStats"] });
      setShowDeleteModal(false);
      setSelectedSubAccount(null);
      toast.success("Sub-account deleted successfully");
    },
    onError: (error) => {
      console.error("Error deleting sub-account:", error);
      if (error.response?.status === 404) {
        toast.error(
          "Sub-account API not available yet. Backend needs to be implemented."
        );
      } else {
        toast.error("Failed to delete sub-account. Please try again.");
      }
    },
  });

  const activateSubAccountMutation = useMutation({
    mutationFn: (subAccountId) => usersAPI.activateSubAccount(subAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["allSubAccountsStats"] });
      toast.success("Sub-account activated successfully");
      setShowEditModal(false);
      setSelectedSubAccount(null);
    },
    onError: (error) => {
      console.error("Error activating sub-account:", error);
      if (error.response?.status === 404) {
        toast.error(
          "Sub-account API not available yet. Backend needs to be implemented."
        );
      } else {
        toast.error("Failed to activate sub-account. Please try again.");
      }
    },
  });

  const deactivateSubAccountMutation = useMutation({
    mutationFn: (subAccountId) => usersAPI.deactivateSubAccount(subAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["allSubAccountsStats"] });
      toast.success("Sub-account deactivated successfully");
      setShowEditModal(false);
      setSelectedSubAccount(null);
    },
    onError: (error) => {
      console.error("Error deactivating sub-account:", error);
      if (error.response?.status === 404) {
        toast.error(
          "Sub-account API not available yet. Backend needs to be implemented."
        );
      } else {
        toast.error("Failed to deactivate sub-account. Please try again.");
      }
    },
  });

  const createSubAccountMutation = useMutation({
    mutationFn: (approvalData) => {
      // Always use the approval endpoint - backend will decide whether to create directly or submit for approval
      return usersAPI.createSubAccountWithApproval(approvalData);
    },
    onSuccess: async (response, variables) => {
      console.log('Create sub-account response:', response);
      const data = response?.data?.data;
      console.log('Extracted data:', data);

      if (data?.requiresApproval) {
        // Submitted for approval
        // Close modal first
        setShowCreateModal(false);
        
        await queryClient.invalidateQueries({ queryKey: ['sub-account-approval-requests'] });
        await queryClient.invalidateQueries({ queryKey: ['sub-account-pending-approvals'] });
        toast.success(data.message || 'Sub-account creation request submitted for approval');
      } else {
        // Created directly (first sub-account)
        console.log('Sub-account created directly, triggering refresh...');

        // Close modal first
        setShowCreateModal(false);

        // Reset to first page to see the new sub-account
        setCurrentPage(1);

        // Wait a moment for state updates
        await new Promise(resolve => setTimeout(resolve, 100));

        // Invalidate queries
        await queryClient.invalidateQueries({ queryKey: ['subAccounts'] });
        await queryClient.invalidateQueries({ queryKey: ['allSubAccountsStats'] });

        console.log('Queries invalidated, data should refresh automatically');

        toast.success(data?.message || 'Sub-account created successfully');
      }
    },
    onError: (error) => {
      console.error('Error submitting sub-account approval request:', error);
      if (error.response?.status === 404) {
        toast.error(
          "Sub-account API not available yet. Backend needs to be implemented."
        );
      } else {
        toast.error(
          "Failed to submit sub-account creation request. Please try again."
        );
      }
    },
  });

  const updateSubAccountMutation = useMutation({
    mutationFn: ({ subAccountId, updateData }) =>
      usersAPI.updateSubAccount(subAccountId, updateData),
    onSuccess: (data) => {
      console.log("Sub-account update response:", data);
      // Invalidate all sub-accounts queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["subAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["allSubAccountsStats"] });
      setShowEditModal(false);
      setSelectedSubAccount(null);
      toast.success("Sub-account updated successfully");
    },
    onError: (error) => {
      console.error("Error updating sub-account:", error);
      if (error.response?.status === 404) {
        toast.error(
          "Sub-account API not available yet. Backend needs to be implemented."
        );
      } else {
        toast.error("Failed to update sub-account. Please try again.");
      }
    },
  });

  // Sub-account approval mutations
  const approveSubAccountMutation = useMutation({
    mutationFn: (requestId) => usersAPI.approveSubAccountRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries(["sub-account-pending-approvals"]);
      queryClient.invalidateQueries(["sub-account-approval-history"]);
      queryClient.invalidateQueries(["subAccounts"]);
      toast.success("Sub-account approved successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to approve sub-account"
      );
    },
  });

  const rejectSubAccountMutation = useMutation({
    mutationFn: (requestId) => usersAPI.rejectSubAccountRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries(["sub-account-pending-approvals"]);
      queryClient.invalidateQueries(["sub-account-approval-history"]);
      toast.success("Sub-account rejected successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to reject sub-account"
      );
    },
  });

  const statuses = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  // Utility function to get sub-account display name
  const getSubAccountDisplayName = (subAccount) => {
    if (
      subAccount.personalInfo?.firstName &&
      subAccount.personalInfo?.lastName
    ) {
      return `${subAccount.personalInfo.firstName} ${subAccount.personalInfo.lastName}`;
    }
    if (subAccount.personalInfo?.fullName) {
      return subAccount.personalInfo.fullName;
    }
    if (subAccount.corporateInfo?.companyName) {
      return subAccount.corporateInfo.companyName;
    }
    if (subAccount.corporateInfo?.primaryAccountHolderName) {
      return subAccount.corporateInfo.primaryAccountHolderName;
    }
    return subAccount.email.split("@")[0];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getStatusBadge = (subAccount) => {
    const status = subAccount.isActive ? "active" : "inactive";
    const statusConfig = {
      active: { label: "Active", class: "badge-success" },
      inactive: { label: "Inactive", class: "badge-error" },
    };
    const config = statusConfig[status] || {
      label: status,
      class: "badge-secondary",
    };
    return <span className={`badge ${config.class}`}>{config.label}</span>;
  };

  const handleSubAccountAction = (action, subAccount) => {
    setSelectedSubAccount(subAccount);

    switch (action) {
      case "edit":
        setShowEditModal(true);
        break;
      case "delete":
        setShowDeleteModal(true);
        break;
      case "view_details":
        setShowDetailsModal(true);
        break;
      default:
        break;
    }
  };

  const handleToggleSubAccountStatus = (subAccount) => {
    setSelectedSubAccount(subAccount);
    setShowToggleStatusModal(true);
  };

  const handleConfirmToggleStatus = (subAccountId, isCurrentlyActive) => {
    if (isCurrentlyActive) {
      deactivateSubAccountMutation.mutate(subAccountId);
    } else {
      activateSubAccountMutation.mutate(subAccountId);
    }
    setShowToggleStatusModal(false);
    setSelectedSubAccount(null);
  };

  // Sub-account approval handlers
  const handleViewSubAccountDetails = (request) => {
    setSelectedApprovalRequest(request);
    setShowDetailsModal(true);
  };

  const handleApproveSubAccount = (request) => {
    setSelectedApprovalRequest(request);
    setApprovalActionType("approve");
    setShowApprovalModal(true);
  };

  const handleRejectSubAccount = (request) => {
    setSelectedApprovalRequest(request);
    setApprovalActionType("reject");
    setShowApprovalModal(true);
  };

  const handleApprovalModalApprove = (requestId) => {
    approveSubAccountMutation.mutate(requestId);
  };

  const handleApprovalModalReject = (requestId, reason) => {
    rejectSubAccountMutation.mutate(requestId);
  };

  const handleApprovalModalViewDetails = (request) => {
    // TODO: Implement view details modal for approval requests
    console.log("View sub-account details:", request);
    toast.success("View details functionality coming soon");
  };

  // Check if current user is the initiator of the request
  const isCurrentUserInitiator = (request) => {
    if (!currentUser || !request.initiatorId) return false;

    const currentUserId = currentUser.id || currentUser._id;
    const initiatorId = request.initiatorId._id || request.initiatorId;

    return currentUserId === initiatorId;
  };

  // Extract sub-accounts from API response
  const subAccounts = subAccountsData?.subAccounts || [];
  const pagination = subAccountsData?.pagination || {};
  const allSubAccounts = allSubAccountsData || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Sub Account Management
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Create and manage sub-accounts with access controls
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {!currentUser?.isSubAccount && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn-primary"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Sub Account
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex -mb-px space-x-8">
            <button
              onClick={() => setActiveTab("sub-accounts")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "sub-accounts"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
              Sub Accounts
            </button>
            <button
              onClick={() => setActiveTab("pending-approvals")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "pending-approvals"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
              Pending Approvals
            </button>
            <button
              onClick={() => setActiveTab("approval-history")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "approval-history"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
              Approval History
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "sub-accounts" && (
          <>
            {/* Sub Account Statistics */}
            <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
              <div className="card">
                <div className="card-body">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 p-3 rounded-lg bg-primary-100">
                      <Users className="w-6 h-6 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0 ml-4">
                      <p className="text-sm font-medium text-gray-600 truncate">
                        Total Sub Accounts
                      </p>
                      <p className="text-2xl font-bold">
                        {allSubAccounts.length}
                      </p>
                      <p className="mt-1 text-xs text-primary-600">
                        Total number of sub accounts
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 p-3 rounded-lg bg-success-100">
                      <CheckCircle className="w-6 h-6 text-success-600" />
                    </div>
                    <div className="flex-1 min-w-0 ml-4">
                      <p className="text-sm font-medium truncate text-secondary-600">
                        Active
                      </p>
                      <p className="text-2xl font-bold">
                        {allSubAccounts.filter((u) => u.isActive).length}
                      </p>
                      <p className="mt-1 text-xs text-success-600">
                        Active sub accounts
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 p-3 rounded-lg bg-warning-100">
                      <UserX className="w-6 h-6 text-warning-600" />
                    </div>
                    <div className="flex-1 min-w-0 ml-4">
                      <p className="text-sm font-medium truncate text-secondary-600">
                        Inactive
                      </p>
                      <p className="text-2xl font-bold">
                        {allSubAccounts.filter((u) => !u.isActive).length}
                      </p>
                      <p className="mt-1 text-xs text-warning-600">
                        Inactive sub accounts
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="card">
              <div className="card-body">
                <div className="flex flex-col gap-4 lg:flex-row">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute w-4 h-4 left-3 top-3 text-dark-gray " />
                      <input
                        type="text"
                        placeholder="Search by name, email, or company..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 form-input"
                      />
                    </div>
                  </div>
                  <div className="lg:w-48">
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="form-input"
                    >
                      <option value="all">All Status</option>
                      {statuses.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Sub Accounts Table */}
            <div className="mt-8 card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">
                  Sub Accounts
                </h3>
              </div>
              <div className="p-0 card-body">
                {subAccounts.length === 0 && !debouncedSearchTerm ? (
                  <div className="p-6 text-center">
                    <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-secondary-500">No sub-accounts found.</p>
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="mt-4 btn-primary"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create First Sub Account
                    </button>
                  </div>
                ) : subAccounts.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="table">
                        <thead className="table-header">
                          <tr>
                            <th className="table-header-cell">Sub Account</th>
                            <th className="table-header-cell">
                              Role & Assignment
                            </th>
                            <th className="table-header-cell">Status</th>
                            <th className="table-header-cell">Created</th>

                            <th className="table-header-cell">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {subAccounts.map((subAccount) => (
                            <tr
                              key={subAccount._id}
                              className="hover:bg-secondary-50"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 w-10 h-10">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-100">
                                      <span className="text-sm font-medium text-primary-600">
                                        {getSubAccountDisplayName(subAccount)
                                          .charAt(0)
                                          .toUpperCase()}
                                        {getSubAccountDisplayName(subAccount)
                                          .split(" ")[1]
                                          ?.charAt(0)
                                          ?.toUpperCase() || ""}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900">
                                      {getSubAccountDisplayName(subAccount)}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {subAccount.email}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex flex-col space-y-1">
                                  <span className="badge badge-info">
                                    {getRoleDisplayName(subAccount.role)}
                                  </span>
                                  {subAccount.assignedRoleId && (
                                    <span className="text-xs badge badge-success">
                                      {subAccount.assignedRoleId.name}
                                    </span>
                                  )}
                                  {!subAccount.assignedRoleId &&
                                    (currentUser?.role === "investor" ||
                                      currentUser?.role === "custodian") && (
                                      <span className="text-xs italic text-gray-400">
                                        No custom role
                                      </span>
                                    )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {getStatusBadge(subAccount)}
                              </td>
                              <td className="px-6 py-4 text-sm whitespace-nowrap text-secondary-500">
                                {formatDate(subAccount.createdAt)}
                              </td>

                              <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() =>
                                      handleSubAccountAction(
                                        "view_details",
                                        subAccount
                                      )
                                    }
                                    className="p-1 text-gray-600 hover:text-gray-700"
                                    title="View Details"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  {!currentUser?.isSubAccount && (
                                      <button
                                        onClick={() =>
                                          handleSubAccountAction(
                                            "edit",
                                            subAccount
                                          )
                                        }
                                        className="p-1 text-gray-600 hover:text-gray-700"
                                        title="Edit Sub Account"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </button>
                                    )}
                                  {!currentUser?.isSubAccount && (
                                      <button
                                        onClick={() => handleToggleSubAccountStatus(subAccount)}
                                        className={subAccount.isActive ? "btn-error btn-sm" : "btn-primary btn-sm"}
                                      >
                                        {subAccount.isActive ? "Deactivate" : "Activate"}
                                      </button>
                                    )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination Controls */}
                    {pagination && pagination.totalPages > 1 && (
                      <div className="px-6 py-3 border-t bg-secondary-50 border-secondary-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm text-secondary-500">
                            <span>
                              Showing{" "}
                              {(currentPage - 1) * pagination.itemsPerPage + 1}{" "}
                              to{" "}
                              {Math.min(
                                currentPage * pagination.itemsPerPage,
                                pagination.totalItems
                              )}{" "}
                              of {pagination.totalItems} results
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="p-2 bg-white border rounded-md border-secondary-300 text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </button>

                            {Array.from(
                              { length: pagination.totalPages },
                              (_, i) => i + 1
                            ).map((page) => (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-3 py-1 rounded-md text-sm font-medium ${page === currentPage
                                    ? "bg-primary-600 text-white"
                                    : "bg-white border border-secondary-300 text-secondary-700 hover:bg-secondary-50"
                                  }`}
                              >
                                {page}
                              </button>
                            ))}

                            <button
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage === pagination.totalPages}
                              className="p-2 bg-white border rounded-md border-secondary-300 text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </>
        )}

        {/* Pending Approvals Tab */}
        {activeTab === "pending-approvals" && (
          <>
            <div className="bg-white border rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Pending Sub-Account Approvals
                </h3>
                <p className="text-sm text-gray-600">
                  Review and approve sub-account creation requests
                </p>
              </div>
              <div>
                {pendingApprovalsData?.data?.data?.requests?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="table">
                      <thead className="table-header">
                        <tr>
                          <th className="table-header-cell">Email</th>
                          <th className="table-header-cell">Role</th>
                          <th className="table-header-cell">Account Type</th>
                          <th className="table-header-cell">Initiated By</th>
                          <th className="table-header-cell">Date</th>
                          <th className="table-header-cell">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pendingApprovalsData.data.data.requests.map(
                          (request) => (
                            <tr key={request._id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {request.targetData?.email || "N/A"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">
                                  {getRoleDisplayName(
                                    request.targetData?.role
                                  ) || "N/A"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">
                                  {request.targetData?.accountType || "N/A"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {request.initiatorId?.email || "N/A"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">
                                  {new Date(
                                    request.createdAt
                                  ).toLocaleDateString()}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() =>
                                      handleViewSubAccountDetails(request)
                                    }
                                    className="p-1 rounded text-primary-600 hover:text-primary-900 hover:bg-blue-50"
                                    title="View Details"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  {(() => {
                                    const isInitiator =
                                      isCurrentUserInitiator(request);
                                    if (isInitiator) return null;
                                    return (
                                      <>
                                        <button
                                          onClick={() =>
                                            handleApproveSubAccount(request)
                                          }
                                          className="btn-primary btn-sm"
                                          title="Approve"
                                        >
                                          Approve
                                        </button>
                                        <button
                                          onClick={() =>
                                            handleRejectSubAccount(request)
                                          }
                                          className="btn-secondary btn-sm"
                                          title="Reject"
                                        >
                                          Reject
                                        </button>
                                      </>
                                    );
                                  })()}
                                </div>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-8 text-center text-gray-500">
                    <UserPlus className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>No pending sub-account approvals</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Approval History Tab */}
        {activeTab === "approval-history" && (
          <div className="bg-white border rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Sub-Account Approval History
              </h3>
              <p className="text-sm text-gray-600">
                View all sub-account approval requests and their status
              </p>
            </div>
            <div>
              {approvalHistoryData?.data?.data?.history?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell">Email</th>
                        <th className="table-header-cell">Role</th>
                        <th className="table-header-cell">Status</th>
                        <th className="table-header-cell">Initiated By</th>
                        <th className="table-header-cell">Initiated At</th>
                        <th className="table-header-cell">
                          Approved/Rejected By
                        </th>
                        <th className="table-header-cell">Action Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {approvalHistoryData.data.data.history.map((request) => (
                        <tr key={request._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {request.targetData?.email || "N/A"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {getRoleDisplayName(request.targetData?.role) ||
                                "N/A"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${request.status === "approved"
                                  ? "bg-green-100 text-green-800"
                                  : request.status === "rejected"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}
                            >
                              {request.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {request.initiatorId?.firstName &&
                                request.initiatorId?.lastName
                                ? `${request.initiatorId.firstName} ${request.initiatorId.lastName}`
                                : request.initiatorId?.email || "N/A"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {request.initiatorId?.email}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {new Date(request.createdAt).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {request.status === "approved" &&
                              request.approvedBy ? (
                              <div>
                                <div className="text-sm text-gray-900">
                                  {request.approvedBy?.firstName &&
                                    request.approvedBy?.lastName
                                    ? `${request.approvedBy.firstName} ${request.approvedBy.lastName}`
                                    : request.approvedBy?.email || "N/A"}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {request.approvedBy?.email}
                                </div>
                              </div>
                            ) : request.status === "rejected" &&
                              request.rejectedBy ? (
                              <div>
                                <div className="text-sm text-gray-900">
                                  {request.rejectedBy?.firstName &&
                                    request.rejectedBy?.lastName
                                    ? `${request.rejectedBy.firstName} ${request.rejectedBy.lastName}`
                                    : request.rejectedBy?.email || "N/A"}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {request.rejectedBy?.email}
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {request.status === "approved" &&
                                request.approvedAt
                                ? new Date(
                                  request.approvedAt
                                ).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                                : request.status === "rejected" &&
                                  request.rejectedAt
                                  ? new Date(
                                    request.rejectedAt
                                  ).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                  : "-"}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">
                  <UserPlus className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No approval history found</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {/* Toggle Status Modal */}
      <ToggleSubAccountStatusModal
        isOpen={showToggleStatusModal}
        onClose={() => {
          setShowToggleStatusModal(false);
          setSelectedSubAccount(null);
        }}
        selectedSubAccount={selectedSubAccount}
        onConfirm={handleConfirmToggleStatus}
        isProcessing={activateSubAccountMutation.isPending || deactivateSubAccountMutation.isPending}
        getSubAccountDisplayName={getSubAccountDisplayName}
      />

      {/* Delete Confirmation Modal */}
      <DeleteSubAccountModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSubAccount(null);
        }}
        selectedSubAccount={selectedSubAccount}
        onConfirm={deleteSubAccountMutation.mutate}
        isDeleting={deleteSubAccountMutation.isPending}
        getSubAccountDisplayName={getSubAccountDisplayName}
      />

      {/* Edit Sub Account Modal */}
      <EditSubAccountModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedSubAccount(null);
        }}
        selectedSubAccount={selectedSubAccount}
        getSubAccountDisplayName={getSubAccountDisplayName}
        getStatusBadge={getStatusBadge}
        onActivate={activateSubAccountMutation.mutate}
        onDeactivate={deactivateSubAccountMutation.mutate}
        onUpdate={(subAccountId, updateData) =>
          updateSubAccountMutation.mutate({ subAccountId, updateData })
        }
        isActivating={activateSubAccountMutation.isPending}
        isDeactivating={deactivateSubAccountMutation.isPending}
        isUpdating={updateSubAccountMutation.isPending}
        currentUserRole={currentUser?.role}
      />

      {/* Sub Account Details Modal */}
      <SubAccountDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedSubAccount(null);
        }}
        selectedSubAccount={selectedSubAccount}
        getSubAccountDisplayName={getSubAccountDisplayName}
        getStatusBadge={getStatusBadge}
      />

      {/* Create Sub Account Modal */}
      <CreateSubAccountModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={createSubAccountMutation.mutate}
        isCreating={createSubAccountMutation.isPending}
        currentUserRole={currentUser?.role}
      />

      {/* Approval Request Modal */}
      <ApproveRequestModal
        isOpen={showApprovalModal}
        onClose={() => {
          setShowApprovalModal(false);
          setSelectedApprovalRequest(null);
        }}
        request={selectedApprovalRequest}
        onApprove={handleApprovalModalApprove}
        onReject={handleApprovalModalReject}
        isSubmitting={
          approveSubAccountMutation.isPending ||
          rejectSubAccountMutation.isPending
        }
        actionType={approvalActionType}
      />

      {/* Sub-Account Details Modal */}
      {showDetailsModal && selectedApprovalRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Sub-Account Request Details
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Information */}
                <div className="p-4 rounded-lg bg-gray-50">
                  <h4 className="mb-4 text-lg font-medium text-gray-900">
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedApprovalRequest.targetData?.email || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Role
                      </label>
                      <p className="text-sm text-gray-900">
                        {getRoleDisplayName(
                          selectedApprovalRequest.targetData?.role
                        ) || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Status
                      </label>
                      <p className="text-sm text-gray-900">
                        Pending L2 Approval
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Requested At
                      </label>
                      <p className="text-sm text-gray-900">
                        {new Date(
                          selectedApprovalRequest.createdAt
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                {selectedApprovalRequest.targetData?.personalInfo && (
                  <div className="p-4 rounded-lg bg-gray-50">
                    <h4 className="mb-4 text-lg font-medium text-gray-900">
                      Personal Information
                    </h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-sm font-medium text-gray-700">
                          First Name
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedApprovalRequest.targetData.personalInfo
                            .firstName || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">
                          Last Name
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedApprovalRequest.targetData.personalInfo
                            .lastName || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">
                          Phone Number
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedApprovalRequest.targetData.personalInfo
                            .phoneNumber || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Request Information */}
                <div className="p-4 rounded-lg bg-gray-50">
                  <h4 className="mb-4 text-lg font-medium text-gray-900">
                    Request Information
                  </h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Requested By
                      </label>
                      <div className="p-3 mt-1 bg-white rounded-md">
                        <p className="text-sm text-gray-900">
                          {selectedApprovalRequest.initiatorId?.firstName &&
                            selectedApprovalRequest.initiatorId?.lastName
                            ? `${selectedApprovalRequest.initiatorId.firstName} ${selectedApprovalRequest.initiatorId.lastName}`
                            : selectedApprovalRequest.initiatorId?.email ||
                            "N/A"}
                        </p>
                        <p className="text-sm text-gray-500">
                          {selectedApprovalRequest.initiatorId?.email}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Account Type
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedApprovalRequest.targetData?.accountType ||
                          "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Assigned Role */}
                {selectedApprovalRequest.targetData?.assignedRoleId && (
                  <div className="p-4 rounded-lg bg-gray-50">
                    <h4 className="mb-4 text-lg font-medium text-gray-900">
                      Assigned Role
                    </h4>
                    <p className="text-sm text-gray-900">
                      Role ID:{" "}
                      {selectedApprovalRequest.targetData.assignedRoleId}
                    </p>
                    <p className="text-sm text-gray-500">
                      Role Model:{" "}
                      {selectedApprovalRequest.targetData.assignedRoleModel ||
                        "N/A"}
                    </p>
                  </div>
                )}

                {/* Comments */}
                {selectedApprovalRequest.comments && (
                  <div className="p-4 rounded-lg bg-gray-50">
                    <h4 className="mb-4 text-lg font-medium text-gray-900">
                      Comments
                    </h4>
                    <p className="text-sm text-gray-900">
                      {selectedApprovalRequest.comments}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubAccountManagement;
